// Minimal IndexedDB wrapper for offline-first caching and the sync queue.

const DB_NAME = "tailleur_pro";
const DB_VERSION = 1;
const STORES = ["clients", "queue", "photoQueue", "meta"];

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      STORES.forEach((s) => {
        if (!db.objectStoreNames.contains(s)) {
          db.createObjectStore(s, { keyPath: s === "clients" ? "id" : "key" });
        }
      });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(store, mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const os = t.objectStore(store);
    const result = fn(os);
    t.oncomplete = () => resolve(result?._value ?? result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

function reqValue(request) {
  const holder = {};
  request.onsuccess = () => (holder._value = request.result);
  return holder;
}

export const idb = {
  async getAll(store) {
    return tx(store, "readonly", (os) => reqValue(os.getAll()));
  },
  async get(store, key) {
    return tx(store, "readonly", (os) => reqValue(os.get(key)));
  },
  async put(store, value) {
    return tx(store, "readwrite", (os) => os.put(value));
  },
  async delete(store, key) {
    return tx(store, "readwrite", (os) => os.delete(key));
  },
  async clear(store) {
    return tx(store, "readwrite", (os) => os.clear());
  },
};

// ---- Clients cache ----
export async function cacheClients(clients) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction("clients", "readwrite");
    const os = t.objectStore("clients");
    os.clear();
    clients.forEach((c) => os.put(c));
    t.oncomplete = () => resolve(true);
    t.onerror = () => reject(t.error);
  });
}
export async function cacheClient(client) {
  return idb.put("clients", client);
}
export async function getCachedClients() {
  return (await idb.getAll("clients")) || [];
}
export async function getCachedClient(id) {
  return idb.get("clients", id);
}
export async function removeCachedClient(id) {
  return idb.delete("clients", id);
}

// ---- Mutation queue ----
export async function enqueue(mutation) {
  const key = mutation.key || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  await idb.put("queue", { key, ...mutation, ts: Date.now() });
  return key;
}
export async function getQueue() {
  const items = (await idb.getAll("queue")) || [];
  return items.sort((a, b) => a.ts - b.ts);
}
export async function dequeue(key) {
  return idb.delete("queue", key);
}

// ---- Photo upload queue (stores the blob) ----
export async function enqueuePhoto(item) {
  const key = `photo-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  await idb.put("photoQueue", { key, ...item, ts: Date.now() });
  return key;
}
export async function getPhotoQueue() {
  const items = (await idb.getAll("photoQueue")) || [];
  return items.sort((a, b) => a.ts - b.ts);
}
export async function dequeuePhoto(key) {
  return idb.delete("photoQueue", key);
}

export async function pendingCount() {
  const q = (await idb.getAll("queue")) || [];
  const p = (await idb.getAll("photoQueue")) || [];
  return q.length + p.length;
}

export async function clearAll() {
  await Promise.all(STORES.map((s) => idb.clear(s).catch(() => {})));
}
